import {useLocation, useNavigate} from "react-router-dom";
import {useCallback, useEffect, useState} from "react";
import FlashQuestionDontKnowDialog from "../../components/dialogs/courses/flash/FlashQuestionDontKnowDialog";
import {kInternalRoutes} from "../../constants/internalRoutes";
import {isNullOrUndefined} from "../../services/Validator";
import {kExerciserFetchError} from "../../constants/errors";
import SystemAppContentError from "../../components/errors/SystemAppContentError";
import {get_nested} from "../../services/JsonHandler";
import {Helmet} from "react-helmet";
import {buildHelmetTitle} from "../../helpers/SeoHelper";
import NeoFlashProgressBar from "../../components/bars/flashScreen/NeoFlashProgressBar";
import SystemAppContentLoader from "../../components/loaders/SystemAppContentLoader";
import {get_exerciser_problem} from "../../api/levelA";
import {isNumber} from "chart.js/helpers";
import NeoExerciserTopBar from "../../components/bars/exerciserScreen/NeoExerciserTopBar";
import ExerciserContentLayout from "../../components/layouts/courses/exercisers/ExerciserContentLayout";
import ExerciserContentMetaLayout from "../../components/layouts/courses/exercisers/ExerciserContentMetaLayout";
import {kActiveExercisers} from "../../constants/exercisers";
import NeoExerciserResultsScreen from "./NeoExerciserResultsScreen";
import SystemLeaveExerciserDialog from "../../components/dialogs/SystemLeaveExerciserDialog";

export default function NeoMetaExerciserScreen() {
    // Used through the app to manage a Flash from questions
    // to results screens

    // PROPERTIES
    // ---- Navigation
    const location = useLocation()
    const origin = location.state?.origin
    const levelAIdentifier = location.state?.levelAIdentifier
    const navigate = useNavigate()
    // ---- Api
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState(null)
    const [exerciserProblem, setExerciserProblem] = useState(null)
    // ---- State
    const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false)
    const [openDontKnowDialog, setOpenDontKnowDialog] = useState(false)
    const [currentIndex, setCurrentIndex] = useState(0)
    const [isExerciserOver, setIsExerciserOver] = useState(false)
    const activeExerciser = kActiveExercisers.find(
        (ex) => ex.id === Number(levelAIdentifier)
    );
    const totalQuestions = get_nested(['questions'], exerciserProblem, []).length

    // HOOKS
    // ---- Memorizes a function, preventing it from being redefined on every render, unless its dependencies change.
    const fetchExerciserProblem = useCallback(() => {
        // Defense
        // ---- Current screen URL identifier parameter
        if (isNullOrUndefined(levelAIdentifier) || !isNumber(levelAIdentifier) || levelAIdentifier < 0) {
            setIsLoading(false)
            setError("Malformed id parameter")
            return
        }

        // ---- Set loader on and clear potential error (retry)
        setIsLoading(true)
        setError(null)

        // Perform
        get_exerciser_problem(levelAIdentifier)
            .then((res) => {
                if (isNullOrUndefined(res))
                    return
                setExerciserProblem(res)
            }).catch((err) => {
            setError(kExerciserFetchError)
            console.error(err)
        }).finally(() => {
            setIsLoading(false)
        })

    }, [levelAIdentifier])
    // ---- At rendering : block straight access, redirect to CoursesScreen
    // ---- OR fetch Flash object from api
    useEffect(() => {
        if (isNullOrUndefined(levelAIdentifier)) {
            navigate(kInternalRoutes.courses)
        } else {
            fetchExerciserProblem()
        }
    }, [fetchExerciserProblem, levelAIdentifier, navigate]);

    // BUILD
    if (!isNullOrUndefined(error))
        return <SystemAppContentError error={error}
                                      isLoading={isLoading}
                                      retryCallback={() => fetchExerciserProblem()} />
    if (isLoading)
        return <SystemAppContentLoader />
    if (isExerciserOver)
        return <NeoExerciserResultsScreen problem={exerciserProblem}
                                          leaveExerciser={leaveFlashSessionCallback} />
    const Component = activeExerciser.component
    return (
        <div className="overflow-auto animate-appear w-full h-[100svh] bg-backgroundLight3 flex flex-col items-center justify-start">
            {/** META */}
            <Helmet>
                <title>{buildHelmetTitle('Ummia - Flash')}</title>
                <meta name="description"
                      content="Maitrise définitivement chaque savoir-faire fondamental" />
            </Helmet>
            {/** MODAL */}
            <SystemLeaveExerciserDialog open={isLeaveModalOpen}
                                        handleClose={processLeaveFlashDialogClosing}
                                        leave={leaveFlashSessionCallback} />
            <FlashQuestionDontKnowDialog open={openDontKnowDialog}
                                         handleClose={() => setOpenDontKnowDialog(false)} />
            {/** TOP BAR */}
            <NeoExerciserTopBar levelA={exerciserProblem.levelA}
                            onButtonClick={onLeaveFlashPressed} />
            <NeoFlashProgressBar currentQuestion={currentIndex + 1}
                                 totalQuestion={totalQuestions} />
            {/** CONTENT */}
            <ExerciserContentMetaLayout>
                <ExerciserContentLayout>
                    <Component problem={exerciserProblem}
                               setIsExerciserOver={setIsExerciserOver}
                               currentIndex={currentIndex}
                               setCurrentIndex={setCurrentIndex} />
                </ExerciserContentLayout>
            </ExerciserContentMetaLayout>
        </div>
    );

    // METHODS
    // ---- NAV : On leave flash pressed
    function onLeaveFlashPressed() {
        if (!isLeaveModalOpen) {
            setIsLeaveModalOpen(true)
        }
    }

    // ---- NAV : Close leave flash modal
    function processLeaveFlashDialogClosing() {
        if (isLeaveModalOpen) {
            setIsLeaveModalOpen(false)
        }
    }

    // ---- LOGIC : Navigate to previous screen
    function leaveFlashSessionCallback() {
        let slug = get_nested(['levelA', 'slug'], exerciserProblem, null)
        if (isNullOrUndefined(slug)) {
            navigate(kInternalRoutes.courses)
        } else {
            navigate(`/savoir-faire/${exerciserProblem.levelA.slug}`, {
                state: {
                    from: origin ?? kInternalRoutes.courses
                }
            })
        }
    }
}